import type { PropertyFilterQuery } from '@cloudscape-design/collection-hooks';
import type { SortingState } from '@risksmart-app/components/src/table/tableUtils';
import { useCallback, useEffect } from 'react';

import { useUpsertUserTablePreferences } from '@/hooks/mutations/user-table-preferences';
import { useGetUserTablePreferences } from '@/hooks/queries';

import type { DefaultSortingState, TableRecord } from '../types';
import type { TableFieldsWithCustomAttributes } from './useAddCustomAttributeFieldData';
import { useFiltersFromUrlHash } from './useFiltersFromUrlHash';

type StorageType<T> = {
  sortingState: SortingState<T> | undefined;
  propertyFilter: PropertyFilterQuery | undefined;
};

/**
 * Store and retrieve filters and sorting in the url after the hash.
 * If the hash does not contain this info, then require from db instead
 *
 * @returns
 */
export const useFiltersFromDBAndUrlHash = <T extends TableRecord>({
  fields,
  defaultSortingState,
  tableId,
  isFieldsLoading = false,
}: {
  fields: TableFieldsWithCustomAttributes<T>;
  defaultSortingState?: DefaultSortingState<T> | undefined;
  tableId?: string;
  // Whether the column/field definitions (incl. async custom-attribute columns)
  // are still loading. Folded into the returned `loading` so the register can
  // withhold the filter until every field a token might reference exists —
  // otherwise a token on a not-yet-loaded custom field is validated away by
  // `useFiltersFromUrlHash` (ENG-583).
  isFieldsLoading?: boolean;
}) => {
  const { data, loading: preferencesLoading } = useGetUserTablePreferences({
    queryArgs: { tableId: tableId ?? '' },
    shouldSkip: !tableId,
  });
  const preferences = data?.user_table_preferences[0]?.Preferences;
  const { upsertUserTablePreferences } = useUpsertUserTablePreferences();
  const result = useFiltersFromUrlHash({
    fields,
    defaultSortingState,
    hasTokenGroupsEnabled: true,
  });
  const { setPropertyFilter, setSortingState, sortingState, propertyFilter } =
    result;

  const saveFilterAndSortState = useCallback(
    ({
      sortingState,
      propertyFilter,
    }: {
      sortingState: SortingState<T> | undefined;
      propertyFilter: PropertyFilterQuery | undefined;
    }) => {
      if (tableId) {
        const state: StorageType<T> = { sortingState, propertyFilter };
        // Fire-and-forget, but the rejection still has to be OBSERVED. `void`
        // discards the promise without handling it, so a network-level failure
        // escaped as an unhandled rejection that Sentry captured as
        // `handled: no` (ENG-728). Nothing upstream catches it — QueryCache.onError
        // sees only queries, and there is no MutationCache.
        //
        // Swallowed rather than logged, deliberately. The caller has already
        // applied this state locally, so a failed write only means it isn't
        // restored on a later visit — not worth a toast. And there is nothing
        // left to record: Sentry's automatic fetch breadcrumb already captures
        // the failed POST, so it appears in the trail of any error that IS
        // reported. A browser-console warning would ship to production (the
        // Vite build strips none) and be read by nobody.
        upsertUserTablePreferences({
          Preferences: state,
          TableId: tableId,
        }).catch(() => undefined);
      }
    },
    [tableId, upsertUserTablePreferences]
  );

  const setSortingStateInStorage = useCallback(
    (sortingState: SortingState<T>) => {
      setSortingState(sortingState);
      saveFilterAndSortState({ sortingState, propertyFilter });
    },
    [propertyFilter, saveFilterAndSortState, setSortingState]
  );

  const setPropertyFilterInStorage = useCallback(
    (propertyFilter: PropertyFilterQuery) => {
      setPropertyFilter(propertyFilter);
      saveFilterAndSortState({ propertyFilter, sortingState });
    },
    [saveFilterAndSortState, setPropertyFilter, sortingState]
  );

  useEffect(() => {
    if (!preferences || result.sortingState || result.propertyFilter) {
      return;
    }

    if (preferences.propertyFilter || preferences.sortingState) {
      result.setPropertyFilterAndSortingState({
        propertyFilter: preferences.propertyFilter,
        sortingState: preferences.sortingState,
      });
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [preferencesLoading, fields]);

  return {
    ...result,
    loading: isFieldsLoading || preferencesLoading,
    setPropertyFilter: setPropertyFilterInStorage,
    setSortingState: setSortingStateInStorage,
  };
};
